import axios from 'axios';
import {Parser} from 'json2csv';
import * as XLSX from "xlsx";
import * as Sentry from '@sentry/browser';
import ApiConfig from './ApiConfig';


export default class Api {
    constructor(options = {}) {
        this.client = axios.create({
            baseURL: process.env.REACT_APP_API_URL || process.env.API_URL,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        this.token = options.token;
        this.onUnauthorized = options.onUnauthorized; // Callback для обработки 401 ошибки

        this.client.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                // Обрабатываем ошибку 401 (Unauthorized) - протухший или невалидный токен
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized: Token expired or invalid');

                    // Очищаем токены
                    localStorage.removeItem("AUTH_TOKEN");
                    localStorage.removeItem("IS_REMEMBER");
                    sessionStorage.removeItem("AUTH_TOKEN");

                    // Вызываем callback для logout через глобальный конфиг
                    ApiConfig.executeLogout();

                    // Если передан callback в options, тоже вызываем его
                    if (typeof this.onUnauthorized === 'function') {
                        this.onUnauthorized();
                    }
                } else if (error.response && error.response.status >= 500) {
                    // Report server errors to Sentry
                    Sentry.captureException(error, {
                        tags: {
                            api_status: error.response.status,
                            api_url: error.config && error.config.url
                        }
                    });
                } else if (!error.response) {
                    // Report network errors to Sentry
                    Sentry.captureException(error, {
                        tags: {
                            error_type: 'network_error',
                            api_url: error.config && error.config.url
                        }
                    });
                }

                return Promise.reject(error);
            }
        );


        this.client.interceptors.request.use(config => {
            if (this.token) {
                config.headers.Authorization = `Bearer ${this.token}`;
            }

            // IMPORTANT: для FormData не задаём Content-Type вручную,
            // иначе может сломаться multipart boundary.
            if (typeof FormData !== "undefined" && config.data instanceof FormData) {
                if (config.headers && config.headers["Content-Type"]) {
                    delete config.headers["Content-Type"];
                }
                if (config.headers && config.headers["content-type"]) {
                    delete config.headers["content-type"];
                }
            }

            if (config.method && typeof config.method === "string") {
                if (config.method.toLowerCase() === "get") {
                    delete config.data;
                }
            }

            return config;
        }, e => Promise.reject(e));



    }

    //запрос авторизации
    async login({username, password, code}) {

        let form = {username, password};

        if (code)
            form.code = code;

        const data = await this.client.post('/login', form);

        this.token = data.data.token;

        return data.data
    }

    //логаут
    logout() {
        // Логируем выход на сервере (fire-and-forget)
        if (this.token) {
            this.client.post('/account/logout', {}).catch(() => {});
        }
        this.token = null;
        localStorage.removeItem("AUTH_TOKEN");
    }

    //запрос данных авторизованного юзера
    async getUserData() {
        return this.client('/account/dashboard').then(({data}) => data);
    }

    //запрос данных OTP
    async getOTPData() {
        return this.client('/account/otp').then(({data}) => data);
    }

    //запрос актуальных прав пользователя (обновление прав при перезагрузке)
    async getPermissions() {
        return this.client.get('/account/permissions').then(({data}) => data);
    }

    //запрос всех данных Dashboard
    async getDashboard() {
        return this.client('/transactions/dashboard').then(({data}) => data);
    }

    //запрос данных персонального менеджера
    async getPersonalManager() {
        return this.client('/account/dashboard').then(({data}) => data.manager);
    }

    // Отчёт по транзакциям
    getProviderSummary(filter) {
        return this.client.post("/statistics/provider-summary-transaction", filter)
            .then(({data}) => data);
    }

    // Отчёт по мерчантам
    getProviderSummaryMerchant(filter) {
        return this.client.post("/statistics/provider-summary-merchant", filter)
            .then(({data}) => data);
    }

    //запрос списка проектов и их данные
    async getProjects() {
        return this.client('/merchant/dashboard').then(({data}) => data);
    }

    //запрос списка статусов
    async getTransactionsStatusList() {
        return this.client('/transactions/dashboard').then(({data}) => data.total);
    }

    //загрузка реестра
    // Описание колонок реестра под выбранного провайдера: по нему кабинет
    // рисует инструкцию, а бэкенд собирает такой же xlsx-образец. Держать этот
    // список на фронте нельзя — он расходится с парсером.
    async getRegistryColumns(type, provider) {
        const params = {type: type};
        if (provider) params.provider = provider;

        const {data} = await this.client.get('/merchant/payout/registry/columns', {params});

        return data && data.data ? data.data : null;
    }

    async registryUpload(data, merchantUuid = null) {
        if (merchantUuid) {
            data.append('merchantUuid', merchantUuid);
        }
        return this.client.post(`/merchant/payout/registry/upload`, data).then(({data}) => data);
    }

    //подтверждение выполнения загруженного реестра
    async registryProcess(registry, uuid) {
        const {data} = await this.client.post(`/merchant/${uuid}/payout/registry/process`, registry);
        return data;
    }

    //загрузка мобильного реестра (СБП/телефон)
    async mobileRegistryUpload(data, merchantUuid = null) {
        if (merchantUuid) {
            data.append('merchantUuid', merchantUuid);
        }
        return this.client.post(`/merchant/payout/mobile-registry/upload`, data).then(({data}) => data);
    }

    //подтверждение выполнения мобильного реестра
    async mobileRegistryProcess(registry, uuid) {
        const {data} = await this.client.post(`/merchant/${uuid}/payout/mobile-registry/process`, registry);
        return data;
    }

    //список мобильных реестров
    async setMobileRegistryList(filters) {
        return this.client.post('/merchant/payout/mobile-registry/list', filters).then(({data}) => data);
    }

    //список банков-участников СБП
    async getSbpParticipants(uuid) {
        return this.client.get(`/merchant/${uuid}/sbp-participants`).then(({data}) => data);
    }

    // Скачать исходный реестр (то, что хранится в базе, восстановленное в XLSX)
    async downloadRegistryFile(registryId, fileName = null) {
        const res = await this.client.get(`/merchant/payout/registry/${registryId}/download`, {
            responseType: "arraybuffer",
        });

        const contentType =
            (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        const blob = new Blob([res.data], { type: contentType });

        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);

        const safeName = fileName
            ? (fileName.toLowerCase().endsWith(".xlsx") ? fileName : `${fileName}.xlsx`)
            : `registry_${registryId}.xlsx`;

        a.download = safeName;
        a.click();

        URL.revokeObjectURL(a.href);
    }


    async getTags(uuid) {
        return this.client(`/merchant/${uuid}/payout/tags`).then(({data}) => data);
    }

    async getAllTags() {
        return this.client('/merchant/payout/tags').then(({data}) => data);
    }

    async getMpsChart() {
        return this.client('/merchant/chart/mps').then(({data}) => data);
    }

    async getTopErrorsDiffChart() {
        return this.client('/merchant/chart/transactions').then(({data}) => data);
    }

    async getCountryTopChart() { // Deleted
        return this.client('/merchant/chart/countries').then(({data}) => data);
    }

    async getCountryMapChart() {
        return this.client('/merchant/datamap/map').then(({data}) => data);
    }

    async getCountryTopData() {
        return this.client('/merchant/datamap/data').then(({data}) => data);
    }

    async setTransactionsChartFilters(filters) {

        let clone = JSON.parse(JSON.stringify(filters));

        clone.filters.forEach(filter => {
            delete filter.locale;
        })

        return this.client.post('/statistics/transactions/charts', clone).then(({data}) => data);
    }

    async setMerchantsTurnoverChart(filters) {
        const clone = JSON.parse(JSON.stringify(filters));

        if (clone.filters) {
            clone.filters.forEach((filter) => {
                delete filter.locale;
            });
        }

        return this.client.post('/statistics/transactions/merchants-turnover', clone).then(({data}) => data);
    }

    async setTransactionsFilters(filters) {

        let clone = JSON.parse(JSON.stringify(filters));

        clone.filters.forEach(filter => {
            delete filter.locale;
        })

        return this.client.post('/statistics/transactions', clone).then(({data}) => data);
    }

    async setRegistryList(filters) {
        return this.client.post('/merchant/payout/registry/list', filters).then(({data}) => data);
    }

    async grantAdmin(id) {
        return this.client.post(`/account/${id}/grant-admin`, {}).then(({data}) => data);
    }

    async revokeAdmin(id) {
        return this.client.post(`/account/${id}/revoke-admin`, {}).then(({data}) => data);
    }

    async getTerminalLimits(projectUuid, terminalId) {
        return this.client(`/merchant/${projectUuid}/terminals/${terminalId}/limits`);
    }

    async updateTerminalLimits(projectUuid, terminalId, payload) {
        return this.client.post(`/merchant/${projectUuid}/terminals/${terminalId}/limits`, payload);
    }

    async setRegistryListFilters(filters) {
        return this.client.post('/merchant/payout/registry/list', filters).then(({data}) => data);
    }

    async setPayoutsList(id) {
        return this.client(`/merchant/payout/registry/${id}/transactions`).then(({data}) => data);
    }

    async setRegistryReprocess(id) {
        return this.client(`/merchant/payout/registry/${id}/reprocess`).then(({data}) => data);
    }

    // Повтор только неуспешных транзакций реестра (с оригинальными PAN, на бэке)
    async registryRetryFailed(id) {
        return this.client.post(`/merchant/payout/registry/${id}/retry-failed`, {}).then(({data}) => data);
    }

    // Подтверждение отправки реестра в обработку (для retry)
    async registryReprocess(id) {
        return this.client.get(`/merchant/payout/registry/${id}/reprocess`).then(({data}) => data);
    }

    async getProjectData(uuid) {
        return this.client(`/merchant/${uuid}`).then(({data}) => data);
    }

    async createMobilePay({ amount, bank, currency = "RUB", widg, unlid, provider }) {
        return this.client
            .post("/api/hydra/mobile-pay", {
                amount,
                currency,
                bank,
                widg,
                unlid,
                provider,
            })
            .then(({ data }) => data);
    }

    async checkWidgetStatus({ widg, unlid }) {
        return this.client
            .get("/api/hydra/widget-status", {
                params: { widg, unlid },
            })
            .then(({ data }) => data);
    }

    async getWidgetConfig({ widg }) {
        return this.client
            .get("/api/hydra/widget-config", {
                params: { widg },
            })
            .then(({ data }) => data);
    }

    // Данные публичной страницы оплаты NPay (QR, суммы, статус, срок) по НЕугадываемому токену.
    async getNpayPage(token) {
        return this.client.get(`/api/npay/page/${token}`).then(({ data }) => data);
    }




    // Формирование и получение отчетов
    async getReport(type, filter, filename, format = "csv") {
        function csvConstruct(data, name) {
            const universalBOM = "\uFEFF";
            const json2csv = new Parser({delimiter: ";", fields: data.fields});
            const csv = json2csv.parse(data.data);

            const a = document.createElement("a");
            a.setAttribute("href", "data:text/csv; charset=utf-8," + encodeURIComponent(universalBOM + csv));
            a.setAttribute("download", `${name}.csv`);
            document.body.appendChild(a);
            a.click();
        }

        function excelConstruct(data, name, format) {
            const worksheet = XLSX.utils.json_to_sheet(data.data, {header: data.fields});
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

            const wbout = XLSX.write(workbook, {bookType: format, type: "array"});

            const blob = new Blob([wbout], {
                type:
                    format === "xlsx"
                        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        : "application/vnd.ms-excel",
            });

            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `${name}.${format}`;
            a.click();
            URL.revokeObjectURL(a.href);
        }

        function OneSConstruct(data, name) {
            const a = document.createElement("a");
            a.setAttribute("href", `data:text/plain;charset=cp1251;base64,${data}`);
            a.download = `${name}.txt`;
            a.click();
        }

        // Запрос данных
        switch (type) {
            case "singleRegistry":
                return this.client(`/merchant/payout/registry/${filter}/report`).then(({data}) => {
                    format === "csv" ? csvConstruct(data, filename) : excelConstruct(data, filename, format);
                });

            case "payout":
                return this.client.post(`/merchant/payout/registry/report`, filter).then(({data}) => {
                    format === "csv" ? csvConstruct(data, filename) : excelConstruct(data, filename, format);
                });

            case "payout1C":
                return this.client.post(`/merchant/payout/registry/report1c`, filter).then(({data}) => {
                    OneSConstruct(data, filename);
                });

            case "transactions":
                return this.client.post(`/statistics/transactions/report`, filter).then(({data}) => {
                    format === "csv" ? csvConstruct(data, filename) : excelConstruct(data, filename, format);
                });

            case "transactions1C":
                return this.client.post(`/statistics/transactions/report1c`, filter).then(({data}) => {
                    OneSConstruct(data, filename);
                });

            default:
                alert("check ReportWidget");
        }
    }


    // Разовая выплата
    async makeSinglePayout(uuid, data) {

        function clean(data) {

            const clone = Object.assign({}, data);

            Object.entries(clone).map(([key, val]) => {
                if (!val)
                    delete clone[key];

                if (key === 'pan' && val)
                    clone.pan = clone.pan.replace(/\s/g, '');

                return true;
            });

            return clone;
        }

        const cleaned = clean(data);
        return this.client.post(`/merchant/${uuid}/payout/single`, cleaned).then(({data}) => data);
    }

    //Запрос статуса выаолнения реестра по ID
    async getRegistryStatus(id) {
        return this.client(`/merchant/payout/registry/${id}/data`).then(({data}) => data);
    }

    //Сохранение данных пользователя
    async setUserData(form) {

        delete form.firstName;
        delete form.lastName;
        delete form.patronymic;

        return this.client.post('/account/update', form).then(({data}) => data);
    }

    async sendConfirmationEmailCode(code) {
        return this.client(`/account/email-confirmation/${code}`).then(({data}) => data);
    }

    //Запрос статуса отмены/возврата транзакции
    async getTransactionStatus(transactionId) {
        return this.client(`/transaction/status/${transactionId}`).then(({data}) => data);
    }

    //Запрос отмены/возврата
    async makeTransactionCancel(transactionId, amount) {
        return this.client.post('/transaction/cancel/', {transactionId, amount}).then(({data}) => data);
    }

    //Обновление имени и меток реестра
    async updateRegistryData(id, form) {
        return this.client.post(`/merchant/payout/registry/${id}/edit`, form).then(({data}) => data);
    }

    // Запрос сброса пароля
    async passwordResetRequest(identifier) {
        return this.client
            .post("/password-reset/request", { identifier })
            .then(function (r) {
                return r.data;
            });
    }

    // Запрос изменения пароля по коду
    async passwordResetVerify(identifier, code) {
        return this.client
            .post("/password-reset/verify", { identifier, code })
            .then(function (r) {
                return r.data;
            });
    }


    //Создание контракта
    async createMetalContract(form) {
        return this.client.post(`/merchant/psa/contracts`, form)
    }

    //Изменение контракта
    async updateMetalContract(id, form) {
        return this.client.post(`/merchant/psa/contracts/${id}`, form)
    }

    //Удаление контракта
    async deleteMetalContract(id) {
        return this.client.delete(`/merchant/psa/contracts/${id}`)
    }

    //ПОлучение контракта по ID
    async getMetalContract(id) {
        return this.client(`/merchant/psa/contracts/${id}`).then(({data}) => data);
    }

    //Все контракты
    async getMetalContracts() {
        return this.client.get(`/merchant/psa/contracts`).then(({data}) => data)
    }

    //Список контрактов
    async getListMetalContracts(data) {
        return this.client.get(`/merchant/psa/contracts`, {
            params: {
                page: data.page,
                itemsPerPage: data.itemsPerPage,
                projects: data.projects
            }
        }).then(({data}) => data);
    }

    //Создание ПСА
    async createPsa(form) {
        return this.client.post('/merchant/psa/items', form)
    }

    //Полуение ПСА
    async getPsa(id) {
        return this.client(`/merchant/psa/items/${id}`).then(({data}) => data)
    }

    //Сохранение ПСА
    async downloadPsa(id) {
        return this.client(`/merchant/psa/items/${id}/xls`, {responseType: 'arraybuffer'})
    }

    //Удаление ПСА
    async deletePsa(id) {
        return this.client.delete(`/merchant/psa/items/${id}`)
    }

    //Получение списка ПСА
    async getPsaItems(data) {
        return this.client(`/merchant/psa/items`, {
            params: data
        }).then(({data}) => data)
    }

    //Обновление настроек двухфакторной авторизации
    async setOTPUpdate(options) {

        const clone = Object.assign({}, options);

        if (clone.OTPPhoneNumber)
            clone.OTPPhoneNumber = Number(clone.OTPPhoneNumber);

        if (clone.code) {
            delete clone.OTPPhoneNumber;
            delete clone.OTPEmail;
            delete clone.OTPProvider;
        }

        return this.client.post('/account/otp', clone).then((response) => response);
    }


    // Закрытие транзакции
    async declineTransaction(transactionId) {
        return this.client.post('/transaction/decline/', {transactionId})
            .then(({data}) => data);
    }

    // перевод в успех

    async payoutTransaction(transactionId) {
        return this.client.post('/transaction/payout/', {transactionId})
            .then(({data}) => data);
    }

    // Повторная отправка уведомления мерчанту по транзакции (только админы).
    // Формат и адрес выбирает бэкенд; для депозита агента это повтор доставки.
    // Отклонённый депозит отвечает 409 с needsConfirmation — повторный вызов
    // с {reopenDeclined: true} открывает его заново и при успехе зачисляет.
    async resendTransactionCallback(transactionId, options = {}) {
        return this.client.post(`/admin/transactions/${transactionId}/resend-callback`, options)
            .then(({data}) => data);
    }


    //Получение списка предоплаченных карт
    async getPreActivatedCardsList(id) {
        return this.client(`/merchant/preactivatedcards/list`).then(({data}) => data);
    }

    //Отправка формы активации карты
    async setPreActivatedCardActivate(ean, form) {

        function convertToUtcDate(date) {
            if (date) {
                const tempdata = new Date(date);
                const year = tempdata.getFullYear();
                const month = tempdata.getMonth();
                const day = tempdata.getDate();
                return new Date(Date.UTC(year, month, day));
            } else return null;
        }

        let clone = JSON.parse(JSON.stringify(form));

        clone.docSerial = form.docSerial.replace(/\s+/g, '');
        clone.gender = form.gender.id;
        clone.birthCountry = form.birthCountry.id;
        clone.docIssuedDate = convertToUtcDate(form.docIssuedDate);
        clone.birthday = convertToUtcDate(form.birthday);
        clone.legalAddress = {
            value: form.legalAddress.value,
            country: form.legalAddress.data.country,
            region: form.legalAddress.data.federal_district,
            district: form.legalAddress.data.region_with_type,
            street: form.legalAddress.data.street_with_type,
            city: form.legalAddress.data.city_with_type != null ? form.legalAddress.data.city_with_type : form.legalAddress.data.region_with_type
        };

        clone.residenceAddress = {
            value: form.residenceAddress.value,
            country: form.residenceAddress.data.country,
            region: form.residenceAddress.data.federal_district,
            district: form.residenceAddress.data.region_with_type,
            street: form.residenceAddress.data.street_with_type,
            city: form.residenceAddress.data.city_with_type != null ? form.residenceAddress.data.city_with_type : form.residenceAddress.data.region_with_type

        };

        return this.client.post(`/merchant/preactivatedcards/${ean}/activate`, clone).then(({data}) => data);
    }

    //Получение списка предоплаченных карт
    async getPreActivatedCardDetails(ean) {
        return this.client(`/merchant/preactivatedcards/${ean}/info`).then(({data}) => data);
    }


    //Administrators API

    // Получение списка аккаунтов
    async getAccountsList(filters) {
        return this.client.post('/accounts/list', filters).then(({data}) => data);
    }


    // Получение списка аккаунтов
    async postFrodList(uuid, filters) {
        return this.client.post(`/merchant/${uuid}/fraud-log`, filters).then(({data}) => data);
    }

    // Получение данных аккаунта
    async getAccountData(id) {
        return this.client(`/account/${id}`).then(({data}) => data);
    }

    // Изминение аккаунта
    async setAccountData(id, data) {
        return this.client.post(`/account/${id}`, data).then(({data}) => data);
    }

    // Установка антифрода
    async setFrodData(uuid, data) {
        return this.client.put(`/merchant/${uuid}/frod`, data).then(({data}) => data);
    }

    // Установка настроек виджета
    async setWidgetData(uuid, data) {
        return this.client.put(`/merchant/${uuid}/widget`, data).then(({data}) => data);
    }

    // Создать новый аккаунт
    async addAccount(data) {
        return this.client
            .post('/account/create', data)
            .then(function(res) {
                return Object.assign({}, res.data, { status: 200 });
            })
            .catch(function(error) {
                var response = (error && error.response) ? error.response : {};
                var responseData = response.data ? response.data : {};
                var status = response.status ? response.status : 500;

                return Object.assign({}, responseData, { status: status });
            });
    }



    // Получение списка проектов
    async getProjectsList(filters) {
        return this.client.post('/merchants/list', filters).then(({data}) => data);
    }

// Поиск аккаунтов
    async makeAccountSearch(filters) {
        return this.client.post('/accounts/search', {search: filters}).then(({data}) => data);
    }

// сброс 2FA
    async resetTwoFactor(accountId) {
        return this.client.post(`/account/${accountId}/reset-2fa`, {}).then(({data}) => data);
    }

    // Проверка транзакции
    async checkTransaction(id) {
        return this.client.get(`/transaction/${id}/check`);
    }

    // Аудит транзакции (полная история событий)
    // Поддерживает поиск по разным полям через searchType: id, uuid, externalRef, hydraId, unlid
    async getTransactionAudit(searchValue, filters = {}) {
        const params = new URLSearchParams();

        // Если указан searchType - используем новый endpoint поиска
        if (filters.searchType && filters.searchType !== 'id') {
            params.set('q', searchValue);
            params.set('searchType', filters.searchType);

            if (filters.eventTypes && filters.eventTypes.length) {
                params.set('eventTypes', filters.eventTypes.join(','));
            }
            if (filters.direction) {
                params.set('direction', filters.direction);
            }

            const url = `/transaction/search/audit?${params.toString()}`;
            return this.client.get(url).then(({data}) => data);
        }

        // Стандартный поиск по ID
        if (filters.eventTypes && filters.eventTypes.length) {
            params.set('eventTypes', filters.eventTypes.join(','));
        }
        if (filters.actors && filters.actors.length) {
            params.set('actors', filters.actors.join(','));
        }
        if (filters.direction) {
            params.set('direction', filters.direction);
        }
        const queryString = params.toString();
        const url = `/transaction/${searchValue}/audit${queryString ? '?' + queryString : ''}`;
        return this.client.get(url).then(({data}) => data);
    }

    // Экспорт аудита транзакции
    async exportTransactionAudit(transactionId, format = 'json') {
        const url = `/transaction/${transactionId}/audit/export?format=${format}`;
        return this.client.get(url, { responseType: 'blob' }).then(({ data }) => data);
    }

    // === ТЕРМИНАЛЫ (общий список + карточка) ===
    async getTerminalsList(filters) {
        // filters: { page, itemsPerPage, order, dateRange, filters: [{key,value}, ...] }
        return this.client.post('/terminals/list', filters).then(({data}) => data);
    }

    async getTerminalData(terminalId) {
        return this.client.get(`/terminals/${terminalId}`).then(({data}) => data);
    }



// Получить баланс из ТКБ по terminalId
    async fetchTerminalBalanceFromTkb(terminalId) {
        return this.client
            .post(`/terminals/${terminalId}/balance/fetch`, {})
            .then(({ data }) => data);
    }

// Применить баланс (в копейках) по terminalId
    async applyTerminalBalance(terminalId, payload) {
        // payload: { balanceMinor, updateTerminal?, forceUpdateTerminal? }
        return this.client
            .post(`/terminals/${terminalId}/balance/apply`, payload)
            .then(({ data }) => data);
    }




    // Изменение суммы транзакции (только админы)
    async changeTransactionAmount(transactionId, amount) {
        return this.client
            .post('/transaction/amount/', { transactionId, amount })
            .then(({ data }) => data);
    }


    // Отправка нового пароля на почту
    async sendNewPassword(id) {
        return this.client.post(`/account/${id}/send-new-password`, {}).then(({data}) => data);
    }

    //   Блокировка мерчанта
    async blockMerchant(uuid) {
        return this.client.post(`/merchant/${uuid}/block`).then(({data}) => data);
    }

    //   Получить список доступных провайдеров (для формы добавления сервиса)
    async getAvailableProviders() {
        return this.client.get(`/merchant/providers/get`).then(({data}) => data);
    }

    //   Получить список сервисов (MID) проекта с методами и комиссиями
    async getProjectServices(uuid) {
        return this.client.get(`/merchant/${uuid}/terminals/full`).then(({data}) => data);
    }

    //   Добавить новый сервис (MID) к проекту
    async addProjectService(uuid, form) {
        return this.client.post(`/merchant/${uuid}/terminals/add`, form).then(({data}) => data);
    }


    //   Обновить методы сервиса (чекбоксы)
    async updateServiceMethods(uuid, terminalId, methods) {
        return this.client.post(`/merchant/${uuid}/terminals/${terminalId}/methods`, {methods})
            .then(({data}) => data);
    }

    //  Обновить комиссии сервиса
    async updateServiceFees(uuid, terminalId, fees) {
        return this.client.post(`/merchant/${uuid}/terminals/${terminalId}/fees`, {fees})
            .then(({data}) => data);
    }

    //  Обновить authData (логин/ключ и т.д.)
    async updateServiceAuthData(uuid, terminalId, authData) {
        return this.client.post(`/merchant/${uuid}/terminals/${terminalId}/auth`, {authData})
            .then(({data}) => data);
    }

    //  Сменить провайдера у сервиса
    async updateServiceProvider(uuid, terminalId, providerUuid) {
        return this.client.patch(`/merchant/${uuid}/terminals/${terminalId}/provider`, {providerUuid})
            .then(({data}) => data);
    }

    //  Удалить сервис (MID)
    async deleteProjectService(uuid, terminalId) {
        return this.client.delete(`/merchant/${uuid}/terminals/${terminalId}`)
            .then(({data}) => data);
    }

    // постмэн
    async hydroPost(data) {
        return this.client.post('/hydra/emulate', data).then(({data}) => data);
    }

// Получить всю инфу по мерчанту
    async getFullMerchant(uuid) {
        return this.client.get(`/merchant/${uuid}/full`).then(({data}) => data);
    }


// ====== MERCHANT SETTINGS (MerchantSettings) ======

// создать настройку (привязка provider + terminal + data)
    async createMerchantSetting(uuid, payload) {
        // payload: { providerUuid, terminalId, enabled?, priority?, data? ... }
        return this.client.post(`/merchant/${uuid}/settings`, payload)
            .then(({data}) => data);
    }

// обновить настройку
    async updateMerchantSetting(uuid, settingId, payload) {
        // payload: любые поля из { providerUuid, terminalId, enabled, priority, fee, isSBP, ... }
        return this.client.post(`/merchant/${uuid}/settings/${settingId}`, payload)
            .then(({data}) => data);
    }

// удалить настройку
    async deleteMerchantSetting(uuid, settingId) {
        return this.client.delete(`/merchant/${uuid}/settings/${settingId}`)
            .then(({data}) => data);
    }

// ====== TERMINALS ======

// создать терминал
    async createTerminal(uuid, payload) {
        // payload: { name?, status?, processing_id?, currency_id?, currency_real_id?, data? }
        // data может включать processingAuth и т.п.
        return this.client.post(`/merchant/${uuid}/terminals`, payload)
            .then(({data}) => data);
    }

// обновить терминал (универсальный PATCH)
    async updateTerminal(uuid, terminalId, payload) {
        return this.client.post(`/merchant/${uuid}/terminals/${terminalId}`, payload)
            .then(({data}) => data);
    }


    // Изминение проекта
// Api.js
    async setProjectData(uuid, data) {
        // аккуратно собираем полезные поля
        const tmp = {
            name: data.name,
            description: data.description,
            url: data.url,
            status: data.status,
            inn: data.inn,
            kpp: data.kpp,
            ogrn: data.ogrn,
        };

        // если accounts есть — добавим, если нет — не трогаем
        if (Array.isArray(data.accounts)) {
            tmp.accounts = data.accounts.map(a => (a && a.id) ? a.id : a);
        }

        return this.client.post(`/merchant/${uuid}/update`, tmp).then(({data}) => data);
    }


    // Создать новый проект
    async addProject(data) {
        return this.client.post('/merchant/create', data).then(({data}) => data);
    }

// Получить схему (если используешь)
    async getUiDescribe() {
        return this.client.post('/postman/describe', {}).then(({data}) => data);
    }

// Мерчанты
    async listMerchants(limit = 1000) {
        return this.client.get('/postman/merchants', {params: {limit}})
            .then(({data}) => data.items);
    }

// Валюты
    async listCurrencies() {
        return this.client.get('/postman/currencies').then(({data}) => data);
    }

// Предпочтительная валюта
    async merchantPreferredCurrency(uuid) {
        return this.client.get(`/postman/merchant/${uuid}/preferred-currency`)
            .then(({data}) => data);
    }

// Последние транзакции
    async latestTransactions(uuid, limit = 5) {
        return this.client.get('/postman/transactions/latest', {params: {merchant: uuid, limit}})
            .then(({data}) => data.items);
    }

// Выполнить действие
    async uiExecute(payload) {
        return this.client.post('/postman/execute', payload).then(({data}) => data);
    }

    // === ANTIFRAUD API ===

    // Получить список записей антифрода
    async getAntiFraudRecords(filters) {
        return this.client.post('/admin/antifraud/records', filters).then(({data}) => data);
    }

    // Получить запись антифрода по ID
    async getAntiFraudRecord(id) {
        return this.client.get(`/admin/antifraud/records/${id}`).then(({data}) => data);
    }

    // Добавить блокировку
    async addAntiFraudRecord(payload) {
        return this.client.put('/admin/antifraud/records', payload).then(({data}) => data);
    }

    // Разблокировать запись
    async unblockAntiFraudRecord(id) {
        return this.client.post(`/admin/antifraud/records/${id}/unblock`).then(({data}) => data);
    }

    // Удалить запись
    async deleteAntiFraudRecord(id) {
        return this.client.delete(`/admin/antifraud/records/${id}`).then(({data}) => data);
    }

    // Получить статистику антифрода
    async getAntiFraudStats() {
        return this.client.get('/admin/antifraud/stats').then(({data}) => data);
    }

    // Получить справочник (sources, kinds)
    async getAntiFraudReference() {
        return this.client.get('/admin/antifraud/reference').then(({data}) => data);
    }

    // Получить логи проверок антифрода
    async getAntiFraudLogs(filters) {
        return this.client.post('/admin/antifraud/logs', filters).then(({data}) => data);
    }

    // === ОСТАНОВЛЕННЫЕ ВЫПЛАТЫ (список карт) ===

    // Выплаты, остановленные по списку карт; status: held | finalized | released | declined | null (все)
    async getPayoutHolds(status) {
        return this.client.get('/admin/payout-holds', { params: status ? { status } : {} }).then(({data}) => data);
    }

    // Провести без отправки провайдеру (списание с баланса, мерчанту — «успех»)
    async finalizePayoutHold(id, comment) {
        return this.client.post(`/admin/payout-holds/${id}/finalize`, { comment }).then(({data}) => data);
    }

    // Передать провайдеру
    async releasePayoutHold(id, comment) {
        return this.client.post(`/admin/payout-holds/${id}/release`, { comment }).then(({data}) => data);
    }

    // Отклонить; comment уходит мерчанту причиной отказа
    async declinePayoutHold(id, comment) {
        return this.client.post(`/admin/payout-holds/${id}/decline`, { comment }).then(({data}) => data);
    }

    // Список карт остановки; all = true — вместе со снятыми
    async getPayoutHoldCards(all) {
        return this.client.get('/admin/payout-holds/cards', { params: all ? { all: 1 } : {} }).then(({data}) => data);
    }

    // Добавить карты: по номеру на строку
    async addPayoutHoldCards(cards, comment) {
        return this.client.post('/admin/payout-holds/cards', { cards, comment }).then(({data}) => data);
    }

    // Снять карту из списка
    async removePayoutHoldCard(id) {
        return this.client.delete(`/admin/payout-holds/cards/${id}`).then(({data}) => data);
    }

    // === MERCHANT ANTIFRAUD CHECK API ===

    // Антифрод: разовая проверка (для мерчанта)
    async antifraudSingleCheck(merchantUuid, data) {
        return this.client.post('/merchant/antifraud/single-check', {
            merchantUuid, ...data
        }).then(({data}) => data);
    }

    // Антифрод: проверка файла (для мерчанта)
    async antifraudBatchCheck(formData) {
        return this.client.post('/merchant/antifraud/batch-check', formData).then(({data}) => data);
    }

    // Антифрод: логи проверок (для мерчанта)
    async getAntiFraudMerchantLogs(filters) {
        return this.client.post('/merchant/antifraud/logs', filters).then(({data}) => data);
    }

    // Антифрод: экспорт логов проверок (для мерчанта)
    async exportAntiFraudLogs(filters) {
        return this.client.post('/merchant/antifraud/logs/export', filters).then(({data}) => data);
    }

    // === PERMISSIONS API ===

    // Получить список всех возможных прав
    async getPermissionsList() {
        return this.client.get('/permissions').then(({data}) => data);
    }

    // Получить права пользователя на проекте
    async getAccountMerchantPermissions(accountId, merchantUuid) {
        return this.client.get(`/account/${accountId}/merchant/${merchantUuid}/permissions`).then(({data}) => data);
    }

    // Обновить права пользователя на проекте
    async updateAccountMerchantPermissions(accountId, merchantUuid, privileges) {
        return this.client.post(`/account/${accountId}/merchant/${merchantUuid}/permissions`, { privileges }).then(({data}) => data);
    }

    // Получить актуальные права текущего пользователя (без перелогина)
    async getMyAccessSettings() {
        return this.client.get('/account/me/access-settings').then(({data}) => data);
    }

    // Получить глобальные права аккаунта
    async getGlobalPermissions(accountId) {
        return this.client.get(`/account/${accountId}/global-permissions`).then(({data}) => data);
    }

    // Обновить глобальные права аккаунта
    async updateGlobalPermissions(accountId, enabled, privileges) {
        return this.client.post(`/account/${accountId}/global-permissions`, {
            enabled, privileges
        }).then(({data}) => data);
    }

    /**
     * Сохранить все данные проекта одним запросом
     * @param {string} uuid - UUID проекта
     * @param {object} payload - Все данные для сохранения:
     *   - basic: { name, description, url, status, inn, kpp, ogrn }
     *   - fraud: { frod: boolean, antifraud: object }
     *   - widget: { widgetKey, widgetCallback }
     *   - users: { add: [accountId, ...], remove: [accountId, ...] }
     *   - settings: { create: [...], update: [...], delete: [settingId, ...] }
     */
    async saveProjectAll(uuid, payload) {
        return this.client.post(`/merchant/${uuid}/save-all`, payload).then(({data}) => data);
    }

    // ============================================
    // AUDIT LOGS - Журнал действий администраторов
    // ============================================

    /**
     * Получить список логов администратора с фильтрами
     * @param {object} filters - Фильтры:
     *   - actorId: number (ID администратора)
     *   - action: string (тип действия)
     *   - entityType: string (тип объекта)
     *   - startDate: string (начало периода)
     *   - endDate: string (конец периода)
     *   - page: number
     *   - itemsPerPage: number
     */
    async getAuditLogs(filters = {}) {
        return this.client.post('/admin/audit/list', filters).then(({data}) => data);
    }

    /**
     * Получить доступные типы действий для фильтра
     */
    async getAuditActions() {
        return this.client.get('/admin/audit/actions').then(({data}) => data);
    }

    /**
     * Получить доступные типы объектов для фильтра
     */
    async getAuditEntityTypes() {
        return this.client.get('/admin/audit/entity-types').then(({data}) => data);
    }

    /**
     * Получить логи по конкретному объекту
     * @param {string} entityType - Тип объекта (account, merchant, terminal, settings)
     * @param {string} identifier - ID или UUID объекта
     */
    async getAuditLogsByEntity(entityType, identifier) {
        return this.client.get(`/admin/audit/entity/${entityType}/${identifier}`).then(({data}) => data);
    }

    /**
     * Получить логи по конкретному администратору
     * @param {number} actorId - ID администратора
     */
    async getAuditLogsByActor(actorId) {
        return this.client.get(`/admin/audit/actor/${actorId}`).then(({data}) => data);
    }

    // ============================================
    // TRANSACTION EVENTS - Логирование событий транзакций
    // ============================================

    /**
     * Получить список событий транзакций с фильтрацией
     * @param {object} filters - Фильтры:
     *   - transactionId: string
     *   - merchantId: string
     *   - eventTypes: array
     *   - actors: array
     *   - direction: string (in/out)
     *   - dateFrom: string (ISO)
     *   - dateTo: string (ISO)
     *   - httpCodeFrom: number
     *   - httpCodeTo: number
     *   - hasError: boolean
     *   - search: string - полнотекстовый поиск
     *   - limit: number (default 50, max 100)
     *   - offset: number
     */
    async getTransactionEvents(filters = {}) {
        return this.client.post('/admin/transaction-events/list', filters).then(({data}) => data);
    }

    /**
     * Получить timeline событий для конкретной транзакции
     * @param {number} transactionId - ID транзакции
     */
    async getTransactionEventsTimeline(transactionId) {
        return this.client.get(`/admin/transaction-events/timeline/${transactionId}`).then(({data}) => data);
    }

    /**
     * Получить список доступных типов событий
     */
    async getTransactionEventTypes() {
        return this.client.get('/admin/transaction-events/event-types').then(({data}) => data);
    }

    /**
     * Получить список доступных actors
     */
    async getTransactionEventActors() {
        return this.client.get('/admin/transaction-events/actors').then(({data}) => data);
    }

    /**
     * Получить список мерчантов для выпадающего списка
     * @param {string} searchQuery - поисковая строка
     */
    async getMerchants(searchQuery = '') {
        const params = new URLSearchParams();
        if (searchQuery) {
            params.set('q', searchQuery);
        }
        return this.client.get(`/admin/transaction-events/merchants?${params.toString()}`).then(({data}) => data);
    }

    /**
     * Экспортировать события в CSV
     * @param {object} filters - Те же фильтры что и getTransactionEvents
     */
    async exportTransactionEvents(filters = {}) {
        return this.client.post('/admin/transaction-events/export', filters, {
            responseType: 'blob'
        }).then(({data}) => data);
    }

    // ============================================
    // DISPUTES - Система диспутов
    // ============================================

    /**
     * Получить список диспутов с фильтрами
     * @param {object} filters - Фильтры:
     *   - filters: array - фильтры по статусу [{new: true}, {in_progress: true}]
     *   - merchants: array - UUID мерчантов
     *   - dateRange: { startDate, endDate }
     *   - search: string - поиск по ID транзакции или тексту
     *   - page: number
     *   - itemsPerPage: number
     */
    async getDisputesList(filters = {}) {
        return this.client.post('/admin/disputes/list', filters).then(({data}) => data);
    }

    /**
     * Получить диспут по ID
     * @param {number} id - ID диспута
     */
    async getDispute(id) {
        return this.client.get(`/admin/disputes/${id}`).then(({data}) => data);
    }

    /**
     * Обновить диспут (статус, примечание, привязка транзакции)
     * @param {number} id - ID диспута
     * @param {object} payload - Данные для обновления:
     *   - status: string (new, in_progress, resolved, archived)
     *   - note: string - примечание
     *   - transactionId: number - ID транзакции для привязки
     */
    async updateDispute(id, payload) {
        return this.client.patch(`/admin/disputes/${id}`, payload).then(({data}) => data);
    }

    /**
     * Проверить статус у провайдера для диспута
     * @param {number} id - ID диспута
     */
    async checkDisputeProvider(id) {
        return this.client.post(`/admin/disputes/${id}/check-provider`, {}).then(({data}) => data);
    }

    /**
     * Проверить статус транзакции по ID (для ручного ввода)
     * @param {number} transactionId - ID транзакции
     */
    async checkDisputeTransaction(transactionId) {
        return this.client.get(`/admin/disputes/check-transaction/${transactionId}`).then(({data}) => data);
    }

    /**
     * Получить справочники для диспутов (статусы, мерчанты)
     */
    async getDisputeReference() {
        return this.client.get('/admin/disputes/reference').then(({data}) => data);
    }

    /**
     * Получить статистику диспутов
     */
    async getDisputeStats(filters = {}) {
        return this.client.post('/admin/disputes/stats', filters).then(({data}) => data);
    }

    /**
     * Получить маппинг чатов на мерчантов
     */
    async getDisputeChatMapping() {
        return this.client.get('/admin/disputes/chat-mapping').then(({data}) => data);
    }

    /**
     * Обновить маппинг чата на мерчанта
     * @param {object} payload - Данные маппинга:
     *   - chatId: string - ID чата Telegram
     *   - merchantId: number - ID мерчанта
     *   - chatTitle: string - Название чата (опционально)
     *   - delete: boolean - Удалить маппинг (опционально)
     */
    async updateDisputeChatMapping(payload) {
        return this.client.post('/admin/disputes/chat-mapping', payload).then(({data}) => data);
    }

    /**
     * Получить URL вложения диспута
     * @param {number} disputeId - ID диспута
     * @param {number} index - Индекс вложения
     */
    async getDisputeAttachmentUrl(disputeId, index) {
        return this.client.get(`/admin/disputes/${disputeId}/attachment/${index}`).then(({data}) => data);
    }

    // ============================================
    // CRM PAYOUT REQUESTS
    // ============================================

    async getCrmPayoutRequests(payload) {
        return this.client.post('/admin/crm/payout-requests', payload).then(function (r) { return r.data; });
    }

    async createCrmPayoutRequest(payload) {
        return this.client.post('/admin/crm/payout-requests/create', payload).then(function (r) { return r.data; });
    }

    async getCrmPayoutRequest(id) {
        return this.client.get('/admin/crm/payout-requests/' + id).then(function (r) { return r.data; });
    }

    async declineCrmPayoutRequest(id, reason) {
        return this.client.post('/admin/crm/payout-requests/' + id + '/decline', {reason: reason}).then(function (r) { return r.data; });
    }

    async bulkDeclineCrmPayoutRequests(ids, reason) {
        return this.client.post('/admin/crm/payout-requests/bulk-decline', {ids: ids, reason: reason}).then(function (r) { return r.data; });
    }

    async formCrmRegistry(requestIds, name, targetMerchantSettingsId) {
        return this.client.post('/admin/crm/payout-requests/form-registry', {
            requestIds: requestIds, name: name, targetMerchantSettingsId: targetMerchantSettingsId
        }).then(function (r) { return r.data; });
    }

    async retryCrmPayoutRequests(ids) {
        return this.client.post('/admin/crm/payout-requests/retry-failed', {ids: ids}).then(function (r) { return r.data; });
    }

    async getCrmPayoutReference() {
        return this.client.get('/admin/crm/payout-requests/reference').then(function (r) { return r.data; });
    }

    async getCrmPayoutStats(projects, payoutType) {
        return this.client.post('/admin/crm/payout-requests/stats', {
            projects: projects || [], payoutType: payoutType || 'card'
        }).then(function (r) { return r.data; });
    }

    async sendToAntifraudCrmPayoutRequests(ids) {
        return this.client.post('/admin/crm/payout-requests/send-to-antifraud', {ids: ids}).then(function (r) { return r.data; });
    }

    async moveBackToAwaitingCrmPayoutRequests(ids) {
        return this.client.post('/admin/crm/payout-requests/move-back-to-awaiting', {ids: ids}).then(function (r) { return r.data; });
    }

    // format: 'yupay' — реестр в формате провайдера Юпэй, без него — обычная выгрузка
    async downloadCrmRegistryExcel(registryId, format) {
        var path = format === 'yupay' ? '/download-yupay' : '/download';
        return this.client.get('/admin/crm/payout-requests/registry/' + registryId + path, {
            responseType: 'blob'
        }).then(function (r) {
            // Если бэкенд вернул JSON (ошибка баланса) — он придёт как blob с типом application/json
            if (r.data && r.data.type && r.data.type.indexOf('application/json') !== -1) {
                return r.data.text().then(function (text) {
                    var json = JSON.parse(text);
                    var err = new Error(json.message || 'Ошибка');
                    err.responseData = json;
                    throw err;
                });
            }
            return r.data;
        });
    }

    async previewCrmRegistryReport(registryId, formData) {
        return this.client.post('/admin/crm/payout-requests/registry/' + registryId + '/upload-report/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function (r) { return r.data; });
    }

    async uploadCrmRegistryReport(registryId, formData) {
        return this.client.post('/admin/crm/payout-requests/registry/' + registryId + '/upload-report', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function (r) { return r.data; });
    }

    async getCrmRegistries(projects, payoutType) {
        return this.client.post('/admin/crm/payout-registries', {
            projects: projects || [], payoutType: payoutType || 'card'
        }).then(function (r) { return r.data; });
    }

    async disbandCrmRegistry(registryId) {
        return this.client.post('/admin/crm/payout-requests/disband-registry', {registryId: registryId}).then(function (r) { return r.data; });
    }

    async removeFromCrmRegistry(requestIds) {
        return this.client.post('/admin/crm/payout-requests/remove-from-registry', {requestIds: requestIds}).then(function (r) { return r.data; });
    }

    async addToCrmRegistry(registryId, requestIds) {
        return this.client.post('/admin/crm/payout-requests/add-to-registry', {
            registryId: registryId, requestIds: requestIds
        }).then(function (r) { return r.data; });
    }

    async formCrmRegistryByAmount(merchantUuid, targetAmount, name, payoutType) {
        var payload = { merchantUuid: merchantUuid, targetAmount: targetAmount, payoutType: payoutType || 'card' };
        if (name) {
            payload.name = name;
        }
        return this.client.post('/admin/crm/payout-requests/form-registry-by-amount', payload).then(function (r) { return r.data; });
    }

    async moveToSuccessCrmPayoutRequest(id, fee) {
        var payload = {};
        if (fee !== null && fee !== undefined) {
            payload.fee = fee;
        }
        return this.client.post('/admin/crm/payout-requests/' + id + '/move-to-success', payload).then(function (r) { return r.data; });
    }

    async bulkMoveToSuccessCrmPayoutRequests(ids, fee) {
        var payload = { ids: ids };
        if (fee !== null && fee !== undefined) {
            payload.fee = fee;
        }
        return this.client.post('/admin/crm/payout-requests/bulk-move-to-success', payload).then(function (r) { return r.data; });
    }

    // ============================================
    // CRAFTLITE LIMIT
    // ============================================

    async getCraftliteLimitStats() {
        return this.client.get('/admin/craftlite-limit/stats').then(({data}) => data);
    }

    // ============================================
    // SETTLEMENT
    // ============================================

    async getSettlements(filters) {
        return this.client.get('/admin/settlements', { params: filters }).then(function(r) { return r.data; });
    }

    async getSettlementDetail(id) {
        return this.client.get('/admin/settlements/' + id).then(function(r) { return r.data; });
    }

    async previewSettlement(data) {
        return this.client.post('/admin/settlements/preview', data).then(function(r) { return r.data; });
    }

    async createSettlement(data) {
        return this.client.post('/admin/settlements', data).then(function(r) { return r.data; });
    }

    async cancelSettlement(id, data) {
        return this.client.post('/admin/settlements/' + id + '/cancel', data).then(function(r) { return r.data; });
    }

    async addSettlementTransactions(settlementId, transactionIds) {
        return this.client.post('/admin/settlements/' + settlementId + '/add-transactions', {
            transaction_ids: transactionIds
        }).then(function(r) { return r.data; });
    }

    async removeSettlementTransactions(settlementId, transactionIds) {
        return this.client.post('/admin/settlements/' + settlementId + '/remove-transactions', {
            transaction_ids: transactionIds
        }).then(function(r) { return r.data; });
    }

    async createSettlementMultipart(formData) {
        return this.client.post('/admin/settlements', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function(r) { return r.data; });
    }

    async downloadSettlementPaymentProof(id) {
        return this.client.get('/admin/settlements/' + id + '/payment-proof', { responseType: 'blob' }).then(function(r) {
            var disposition = r.headers['content-disposition'] || '';
            var match = disposition.match(/filename="?([^"]+)"?/);
            var filename = match ? match[1] : 'payment_proof';
            var contentType = r.headers['content-type'] || 'application/octet-stream';
            var url = window.URL.createObjectURL(new Blob([r.data], { type: contentType }));
            var link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    async downloadSettlementExcel(id) {
        return this.client.get('/admin/settlements/' + id + '/export-excel', { responseType: 'blob' }).then(function(r) {
            var url = window.URL.createObjectURL(new Blob([r.data]));
            var link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'settlement_' + id + '.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    // ============================================
    // DEPOSITS
    // ============================================

    async getDeposits(filters) {
        return this.client.get('/admin/deposits', { params: filters }).then(function(r) { return r.data; });
    }

    async getDepositDetail(id) {
        return this.client.get('/admin/deposits/' + id).then(function(r) { return r.data; });
    }

    async createDeposit(data) {
        return this.client.post('/admin/deposits', data).then(function(r) { return r.data; });
    }

    async createDepositMultipart(formData) {
        return this.client.post('/admin/deposits', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function(r) { return r.data; });
    }

    async cancelDeposit(id, data) {
        return this.client.post('/admin/deposits/' + id + '/cancel', data).then(function(r) { return r.data; });
    }

    async downloadDepositPaymentProof(id) {
        return this.client.get('/admin/deposits/' + id + '/payment-proof', { responseType: 'blob' }).then(function(r) {
            var disposition = r.headers['content-disposition'] || '';
            var match = disposition.match(/filename="?([^"]+)"?/);
            var filename = match ? match[1] : ('deposit_proof_' + id);
            var url = window.URL.createObjectURL(new Blob([r.data]));
            var link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    // ============================================
    // TERMINAL BALANCES & LEDGER
    // ============================================

    async getTerminalBalances(terminalId, merchantUuid) {
        var config = merchantUuid ? { params: { merchant_uuid: merchantUuid } } : undefined;
        return this.client.get('/admin/terminals/' + terminalId + '/balances', config).then(function(r) { return r.data; });
    }

    async getTerminalLedger(terminalId, filters) {
        return this.client.get('/admin/terminals/' + terminalId + '/ledger', { params: filters }).then(function(r) { return r.data; });
    }

    async createTerminalLedgerEntry(terminalId, data) {
        return this.client.post('/admin/terminals/' + terminalId + '/ledger', data).then(function(r) { return r.data; });
    }

    async updateTerminalBalanceLimits(terminalId, data) {
        return this.client.put('/admin/terminals/' + terminalId + '/limits', data).then(function(r) { return r.data; });
    }

    async updateTerminalSettlementConfig(terminalId, data) {
        return this.client.put('/admin/terminals/' + terminalId + '/settlement-config', data).then(function(r) { return r.data; });
    }

    async getAdminTerminalFees(terminalId) {
        return this.client.get('/admin/terminals/' + terminalId + '/fees').then(function(r) { return r.data; });
    }

    async updateAdminTerminalFees(terminalId, fees) {
        return this.client.put('/admin/terminals/' + terminalId + '/fees', { fees: fees }).then(function(r) { return r.data; });
    }

    // ============================================
    // MERCHANT BALANCE API
    // ============================================

    async getMerchantBalances() {
        return this.client.get('/merchant/balances').then(function(r) { return r.data; });
    }

    async getMerchantStatement(filters) {
        return this.client.get('/merchant/statement', { params: filters }).then(function(r) { return r.data; });
    }

    async getTrafficMonitoring() {
        return this.client.get('/admin/traffic-monitoring').then(function(r) { return r.data; });
    }

    async exportMerchantStatement(params) {
        return this.client.get('/merchant/statement/export', { params: params, responseType: 'blob' }).then(function(r) {
            var url = window.URL.createObjectURL(new Blob([r.data]));
            var link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'statement.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }

    // ------------- Секретные чаты -------------
    //
    // Переписка шифруется на сервере и удаляется через сутки, поэтому здесь
    // ничего не кэшируется: список и сообщения всегда запрашиваются заново,
    // иначе кабинет показывал бы то, чего на сервере уже нет.

    async getSecretChatsUnreadCount() {
        return this.client.get('/account/secret-chats/unread-count').then((r) => r.data);
    }

    async getAdminSecretChatsUnreadCount() {
        return this.client.get('/admin/secret-chats/unread-count').then((r) => r.data);
    }

    async listSecretChats() {
        return this.client.get('/account/secret-chats').then((r) => r.data);
    }

    async createSecretChat(subject, message) {
        return this.client.post('/account/secret-chats', { subject, message }).then((r) => r.data);
    }

    async getSecretChatMessages(id) {
        return this.client.get(`/account/secret-chats/${id}/messages`).then((r) => r.data);
    }

    async postSecretChatMessage(id, body, files) {
        // С файлами уходит форма, без них — обычный JSON: multipart ради
        // одного текста заставлял бы сервер разбирать форму на каждую реплику.
        if (files && files.length > 0) {
            const form = new FormData();
            form.append('body', body || '');
            files.forEach((file) => form.append('files[]', file));

            return this.client.post(`/account/secret-chats/${id}/messages`, form).then((r) => r.data);
        }

        return this.client.post(`/account/secret-chats/${id}/messages`, { body }).then((r) => r.data);
    }

    /**
     * Адрес файла. Скачивается запросом с токеном (см. downloadSecretChatFile):
     * простая ссылка не подойдёт — заголовка авторизации у неё нет.
     */
    secretChatFileUrl(chatId, fileId, asAdmin) {
        const base = asAdmin ? '/admin/secret-chats' : '/account/secret-chats';

        return `${base}/${chatId}/files/${fileId}`;
    }

    async downloadSecretChatFile(chatId, fileId, asAdmin) {
        return this.client
            .get(this.secretChatFileUrl(chatId, fileId, asAdmin), { responseType: 'blob' })
            .then((r) => r.data);
    }

    async readSecretChat(id) {
        return this.client.post(`/account/secret-chats/${id}/read`, {}).then((r) => r.data);
    }

    async listAdminSecretChats() {
        return this.client.get('/admin/secret-chats').then((r) => r.data);
    }

    async listAdminSecretChatAccounts(query) {
        return this.client
            .get('/admin/secret-chats/accounts', { params: query ? { query } : {} })
            .then((r) => r.data);
    }

    async createAdminSecretChat(accountId, subject, message) {
        return this.client
            .post('/admin/secret-chats', { accountId, subject, message })
            .then((r) => r.data);
    }

    async getAdminSecretChatMessages(id) {
        return this.client.get(`/admin/secret-chats/${id}/messages`).then((r) => r.data);
    }

    async postAdminSecretChatMessage(id, body, files) {
        if (files && files.length > 0) {
            const form = new FormData();
            form.append('body', body || '');
            files.forEach((file) => form.append('files[]', file));

            return this.client.post(`/admin/secret-chats/${id}/messages`, form).then((r) => r.data);
        }

        return this.client.post(`/admin/secret-chats/${id}/messages`, { body }).then((r) => r.data);
    }

    async readAdminSecretChat(id) {
        return this.client.post(`/admin/secret-chats/${id}/read`, {}).then((r) => r.data);
    }

    // ------------- Support tickets (merchant widget) -------------

    async listMerchantTickets() {
        return this.client.get('/merchant/tickets').then((r) => r.data);
    }

    async getMerchantTicketsUnreadCount() {
        return this.client.get('/merchant/tickets/unread-count').then((r) => r.data);
    }

    async getMerchantTicket(id) {
        return this.client.get(`/merchant/tickets/${id}`).then((r) => r.data);
    }

    async getMerchantTicketMessages(id, since) {
        const params = since ? { since } : {};
        return this.client.get(`/merchant/tickets/${id}/messages`, { params }).then((r) => r.data);
    }

    async createMerchantTicket(subject, body, files) {
        const created = await this.client.post('/merchant/tickets', { subject, body }).then((r) => r.data);
        if (created && created.first_message_id && (files || []).length > 0) {
            for (const f of files) {
                const fd = new FormData();
                fd.append('file', f);
                await this.client.post(
                    `/merchant/tickets/${created.ticket.id}/messages/${created.first_message_id}/attachments`,
                    fd
                );
            }
        }
        return created;
    }

    async postMerchantTicketMessage(id, body, files) {
        const sent = await this.client.post(`/merchant/tickets/${id}/messages`, { body }).then((r) => r.data);
        if (sent && sent.message && sent.message.id && (files || []).length > 0) {
            for (const f of files) {
                const fd = new FormData();
                fd.append('file', f);
                await this.client.post(
                    `/merchant/tickets/${id}/messages/${sent.message.id}/attachments`,
                    fd
                );
            }
        }
        return sent;
    }

    async editMerchantTicketMessage(id, msgId, body) {
        return this.client.patch(`/merchant/tickets/${id}/messages/${msgId}`, { body }).then((r) => r.data);
    }

    async markMerchantTicketRead(id) {
        return this.client.post(`/merchant/tickets/${id}/read`).then((r) => r.data);
    }

    merchantTicketAttachmentUrl(ticketId, attachmentId) {
        return `${process.env.REACT_APP_API_URL || process.env.API_URL || ''}/merchant/tickets/${ticketId}/attachments/${attachmentId}`;
    }

    async downloadMerchantTicketAttachment(ticketId, attachmentId, filename) {
        const r = await this.client.get(
            `/merchant/tickets/${ticketId}/attachments/${attachmentId}`,
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || 'file');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    // ------------- Support tickets (admin) -------------

    async adminListTickets(params) {
        return this.client.get('/admin/tickets', { params }).then((r) => r.data);
    }

    async adminGetTicket(id) {
        return this.client.get(`/admin/tickets/${id}`).then((r) => r.data);
    }

    async adminGetTicketMessages(id, since) {
        const params = since ? { since } : {};
        return this.client.get(`/admin/tickets/${id}/messages`, { params }).then((r) => r.data);
    }

    async adminPostTicketMessage(id, body, files) {
        const sent = await this.client.post(`/admin/tickets/${id}/messages`, { body }).then((r) => r.data);
        if (sent && sent.message && sent.message.id && (files || []).length > 0) {
            for (const f of files) {
                const fd = new FormData();
                fd.append('file', f);
                await this.client.post(
                    `/admin/tickets/${id}/messages/${sent.message.id}/attachments`,
                    fd
                );
            }
        }
        return sent;
    }

    async adminEditTicketMessage(id, msgId, body) {
        return this.client.patch(`/admin/tickets/${id}/messages/${msgId}`, { body }).then((r) => r.data);
    }

    async adminDeleteTicketMessage(id, msgId) {
        return this.client.delete(`/admin/tickets/${id}/messages/${msgId}`).then((r) => r.data);
    }

    async adminDeleteTicketAttachment(id, aid) {
        return this.client.delete(`/admin/tickets/${id}/attachments/${aid}`).then((r) => r.data);
    }

    async adminDeleteTicket(id) {
        return this.client.delete(`/admin/tickets/${id}`).then((r) => r.data);
    }

    async adminChangeTicketStatus(id, status) {
        return this.client.post(`/admin/tickets/${id}/status`, { status }).then((r) => r.data);
    }

    async adminMarkTicketRead(id) {
        return this.client.post(`/admin/tickets/${id}/read`).then((r) => r.data);
    }

    async adminDownloadTicketAttachment(ticketId, attachmentId, filename) {
        const r = await this.client.get(
            `/admin/tickets/${ticketId}/attachments/${attachmentId}`,
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || 'file');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

}



// WEBPACK FOOTER //
// ./src/Api.js